import { z } from 'zod'
import { DelingId, InstantieId, SectionId } from './id.js'
import { Side, Role } from './identity.js'
import { Moment, Person, compareMoments } from './moment.js'

/**
 * Een deling is de uitnodiging met rechten per sectie (ontwerp 4.8).
 *
 * Het recht hoort bij de deling en niet bij de definitie (R-67, U6): daarom
 * kent de definitie geen rollen. Wie geen recht op een sectie heeft krijgt de
 * inhoud niet aangeleverd, want de deling stuurt de server-projectie en niet
 * alleen de weergave (R-68, ontwerp 5.2).
 *
 * Wat dit schema niet doet: uitmaken of iemand toegang heeft. Een openstaande
 * uitnodiging geeft nog geen toegang; het recht ontstaat pas bij binnenkomen
 * (R-64c). Dat is een regel over gedrag en hij hoort in de projectieberekening
 * (M1) en in de store (M3), niet in de vorm. Het schema laat dus met opzet een
 * record toe met rechten én toestand `uitgenodigd`: dat is precies de stand die
 * de projectie moet negeren.
 *
 * Uitnodigen zelf, het bestaan van een account en de uitnodigingsmail liggen bij
 * de gastheer (R-64b, ontwerp 13.4).
 *
 * Delen gebeurt aan beide kanten (R-64d): de invuller nodigt een medeschrijver
 * uit, de interne kant deelt met een adviseur (R-184). Daarom draagt het record
 * wie hem maakte en aan welke kant die stond. Wat een deling mag geven staat ook
 * hier niet: nooit meer dan de deler zelf heeft (R-65a), en op een toetssectie
 * nooit meer dan lezen (R-65b, R-65c). Die drie hangen af van de definitie en de
 * beurt, die dit schema geen van beide kent, en horen dus in de
 * projectieberekening (M1) en in de store (M3).
 */
/**
 * Het recht per sectie van een deling: lezen of bewerken (R-65, R-67).
 *
 * Eigen naam omdat twee vormen hem dragen: de deling van vóór het register en de toekenning
 * met bron deling (R-155b). Een deling zonder rechten bestaat niet, in geen van beide.
 */
export const SectionPermissions = z
  .record(SectionId, z.enum(['lezen', 'bewerken']))
  .refine((permissions) => Object.keys(permissions).length > 0, {
    message: 'een deling geeft recht op minstens één sectie',
  })
export type SectionPermissions = z.infer<typeof SectionPermissions>

export const Deling = z
  .strictObject({
    id: DelingId,
    instantieId: InstantieId,
    /** Uitnodigen gaat op e-mailadres (R-64). */
    email: z.email(),
    /** Per sectie lezen of bewerken (R-65). Een deling zonder rechten bestaat niet. */
    rechten: SectionPermissions,
    /** Drie toestanden (R-64c). */
    toestand: z.enum(['uitgenodigd', 'binnengekomen', 'geweigerd']),
    /**
     * Het tokensubject achter dit adres, vastgelegd op het moment dat de uitgenodigde
     * antwoordt. Null zolang de uitnodiging openstaat.
     *
     * Dit is het antwoord op de vraag die M1 voor de store openliet
     * (`packages/engine/src/projectie.ts`, punt 2): een deling staat op een e-mailadres
     * (R-64) en het product heeft geen gebruikerstabel (R-154), dus de koppeling naar
     * een persoon loopt via de e-mailclaim uit het token. Dat is een zwakke koppeling,
     * want een adres kan van eigenaar wisselen. Bij binnenkomen is het subject wel
     * bekend, en een moment dat niet wordt vastgelegd is voorgoed weg; daarom staat het
     * hier. De projectieberekening koppelt nog op het adres; overstappen op dit veld is
     * een eigen wijziging in de engine.
     */
    persoon: Person.nullable(),
    /** Wie de deling maakte (R-191). */
    door: Person,
    /**
     * De kant van de maker (R-64d, R-66a). Staat er los bij en wordt niet afgeleid
     * uit het gastheervoorvoegsel van `door`: een NWO-medewerker kan zelf een
     * formulier invullen (R-138), dus het voorvoegsel zegt welke gastheer en niet
     * welke kant. Het overzicht van R-66 filtert hierop.
     */
    doorKant: Side,
    /**
     * De rol van de maker (R-65a, R-65c). Nodig om te kunnen narekenen hoeveel
     * recht de deler zelf had: een deling geeft nooit meer, en wie in de
     * toetserrol handelt geeft nooit bewerkrecht. Uit `doorKant` volgt de rol
     * niet, want een NWO-medewerker kan zelf invullen (R-138) en een externe
     * gebruiker is nooit toetser (B18); kant en rol staan los.
     */
    doorRol: Role,
    uitgenodigdOp: Moment,
    /** Wanneer de uitgenodigde binnenkwam. Null zolang dat niet is gebeurd. */
    binnengekomenOp: Moment.nullable(),
    /**
     * Intrekken beëindigt de deling met het moment erbij (R-66); het record
     * blijft staan voor de herleidbaarheid. Null betekent: niet ingetrokken.
     */
    ingetrokkenOp: Moment.nullable(),
  })
  .superRefine((deling, ctx) => {
    // Toegang ontstaat pas bij binnenkomen (R-64c), dus die twee horen bij elkaar: een
    // binnengekomen deling zonder moment is niet in de tijd te plaatsen, en een moment op
    // een openstaande uitnodiging zou toegang suggereren die er niet is.
    if ((deling.toestand === 'binnengekomen') !== (deling.binnengekomenOp !== null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['binnengekomenOp'],
        message: 'een binnengekomen deling draagt zijn binnenkomstmoment, en alleen die',
      })
    }
    if (deling.toestand === 'uitgenodigd' && deling.persoon !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['persoon'],
        message: 'een openstaande uitnodiging kent de persoon nog niet',
      })
    }
    if (deling.ingetrokkenOp !== null && compareMoments(deling.ingetrokkenOp, deling.uitgenodigdOp) < 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['ingetrokkenOp'],
        message: 'een deling is niet ingetrokken voordat ze bestond',
      })
    }
  })

export type Deling = z.infer<typeof Deling>
